/**
 * RefreshBanner — global "your data is out of date" banner.
 *
 * Spec §16: "Last-write-wins with refresh banner on row_version mismatch."
 *
 * Shows when the QueryClient detects a 409 ROW_VERSION_CONFLICT from any
 * mutation or query. The store is module-level so a conflict surfaced
 * deep inside a route still triggers this banner at the app shell layer
 * (where it belongs — the message is "the app state is stale", not "this
 * specific form failed").
 *
 * The user has two paths:
 *   1. Refresh — calls window.location.reload(), which fetches the latest
 *      state from the server. Recommended; resolves the staleness cleanly.
 *   2. Dismiss — clears the banner without reloading. The user accepts
 *      that they're working against potentially-stale data and can re-try
 *      the failed action manually (the form usually re-fetches on next
 *      query because the mutation invalidated relevant keys).
 *
 * The banner is sticky-positioned at the top of the viewport, above the
 * AppShell content. It's never auto-dismissed — staleness doesn't fix
 * itself.
 */

import { useSyncExternalStore } from 'react';
import { AlertTriangle, RotateCw, X } from 'lucide-react';
import {
  dismiss,
  getSnapshot,
  subscribe,
} from '@/lib/refresh-banner-store';

export function RefreshBanner() {
  const active = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (!active) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex items-center gap-3 border-b border-warning-border bg-warning-subtle px-6 py-2 text-sm text-warning-fg"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <span className="font-medium">This page is out of date.</span>{' '}
        <span className="text-secondary">
          Someone else updated a record you were viewing. Refresh to see the latest version
          before making changes.
        </span>
      </div>
      <button
        type="button"
        onClick={() => {
          dismiss();
          window.location.reload();
        }}
        className="inline-flex items-center gap-1 rounded-md border border-warning-border bg-surface px-2 py-1 text-xs font-medium text-warning-fg hover:bg-warning-subtle"
      >
        <RotateCw className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
        Refresh
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="rounded-md p-1 text-secondary hover:bg-surface hover:text-primary"
      >
        <X className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
      </button>
    </div>
  );
}
